import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Searchbar from "../../components/Search";
import useApiGet from "../hooks/getData";
import useApiPost from "../hooks/postData";
import toast from "react-hot-toast";
import Loader from "../../assets/Loader.gif";
import NoData from "../../assets/NoData.png";

const headCell = "text-headerText font-poppins text-sm font-semibold";

const STATUS_FILTERS = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "All", value: "" },
];

const STATUS_STYLE = {
  pending: "text-[#B7791F] bg-[#B7791F] bg-opacity-20",
  approved: "text-[#0D9947] bg-[#0D9947] bg-opacity-20",
  rejected: "text-red-500 bg-red-500 bg-opacity-20",
};

const formatIST = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const rupees = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function PaymentInfo({ type, details = {} }) {
  if (type === "upi") {
    return (
      <>
        <p className="font-semibold">UPI</p>
        <p className="break-all">{details.upi_id}</p>
        {details.account_holder_name && (
          <p className="text-[#858585]">{details.account_holder_name}</p>
        )}
      </>
    );
  }
  return (
    <>
      <p className="font-semibold">Bank</p>
      <p>{details.account_holder_name}</p>
      <p className="break-all">A/C {details.account_number}</p>
      <p className="text-[#858585]">
        {details.ifsc_code} · {details.bank_name}
        {details.branch_name ? `, ${details.branch_name}` : ""}
      </p>
    </>
  );
}

function PostCoinWithdrawals() {
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);
  const { getData, loading } = useApiGet();
  const { postData } = useApiPost();

  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [actingId, setActingId] = useState(null);

  const load = async () => {
    try {
      const res = await getData("/post-coins/admin/withdrawals", {
        status: status || undefined,
        page,
        limit: 20,
      });
      setResult(res?.data || null);
    } catch (err) {
      toast.error("Failed to load requests");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  const items = result?.items || [];
  const lastPage = result ? Math.max(1, Math.ceil(result.total / result.limit)) : 1;

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  const resolve = async (w, action) => {
    const approve = action === "approve";
    const ok = window.confirm(
      `${approve ? "Approve" : "Reject (coins will be returned)"} request #${w.id} — ${w.coins} coins (${rupees(w.amount)})?`
    );
    if (!ok) return;
    const note = window.prompt("Admin note (optional):", "");
    if (note === null) return;

    setActingId(w.id);
    try {
      const res = await postData(`/post-coins/admin/withdrawals/${w.id}/${action}`, {
        admin_note: note,
      });
      toast.success(res?.message || (approve ? "Request approved" : "Request rejected"));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update the request");
    } finally {
      setActingId(null);
      load();
    }
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />

      <div className="px-4 pb-10 xl:px-6">
        <div className="flex justify-between border-t-[#F2F2F2] py-3">
          <h2 className="text-[#000000] dark:text-darkText font-poppins text-xl font-semibold pt-3">
            Post Coin Withdrawals
          </h2>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link to="/dashboard">
              <h3 className="text-[#3A3A3A] dark:text-darkText font-poppins text-base font-semibold">
                Dashboard
              </h3>
            </Link>
            <div className="rounded-full w-1 h-1 bg-[#E0E0E0]"></div>
            <h3 className="text-[#858585] font-poppins text-base">Post Coin Withdrawals</h3>
          </div>

          <div className="flex items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.label}
                onClick={() => changeStatus(f.value)}
                className={`px-4 py-1.5 text-sm font-poppins rounded-md ${
                  status === f.value
                    ? "text-white bg-button-gradient"
                    : "bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText text-[#000000]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-[#E3E3E3] dark:border-[#1F1F1F] rounded-lg mt-8 overflow-x-auto w-full">
          <div className="min-w-[1200px]">
            <div className="flex px-4 py-3 pl-8 text-left border-b dark:border-b-[#1F1F1F] dark:bg-darkHeader bg-header1">
              <div className={`w-[5%] ${headCell}`}>ID</div>
              <div className={`w-[18%] ${headCell}`}>USER</div>
              <div className={`w-[12%] ${headCell}`}>COINS / AMOUNT</div>
              <div className={`w-[22%] ${headCell}`}>PAY TO</div>
              <div className={`w-[13%] ${headCell}`}>REQUESTED (IST)</div>
              <div className={`w-[10%] ${headCell}`}>STATUS</div>
              <div className={`w-[20%] ${headCell}`}>ACTION</div>
            </div>

            {loading && !result ? (
              <div className="flex justify-center py-32">
                <img src={Loader} alt="loader" height={50} width={50} />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col justify-center py-32 place-items-center">
                <img src={NoData} alt="no data" className="w-96" />
                <h2 className="font-poppins text-[#000000] dark:text-darkText text-sm font-semibold">
                  No requests found
                </h2>
              </div>
            ) : (
              items.map((w, index) => (
                <div
                  key={w.id}
                  className={`${
                    index % 2 === 0 ? "bg-white dark:bg-primary" : "bg-[#00162e0a] dark:bg-primary"
                  } flex items-start px-4 py-3 pl-8 border-b dark:border-b-[#1F1F1F] last:border-0 text-sm font-poppins text-[#414141] dark:text-tableDarkLarge`}
                >
                  <div className="w-[5%]">#{w.id}</div>

                  <div className="w-[18%] pr-2 min-w-0">
                    <p className="font-semibold text-[#000000] dark:text-darkText truncate">
                      {w.user?.name}
                    </p>
                    <p className="text-xs text-[#858585] truncate">@{w.user?.username}</p>
                    <p className="text-xs text-[#858585] truncate">{w.user?.email}</p>
                    {w.user?.phone && <p className="text-xs text-[#858585]">{w.user.phone}</p>}
                  </div>

                  <div className="w-[12%] pr-2">
                    <p className="font-semibold">{Number(w.coins).toLocaleString()} coins</p>
                    <p className="text-[#0D9947] font-semibold">{rupees(w.amount)}</p>
                    <p className="text-xs text-[#858585]">
                      {w.coins_per_unit} = {rupees(w.rupees_per_unit)}
                    </p>
                  </div>

                  <div className="w-[22%] pr-2 min-w-0">
                    <PaymentInfo type={w.payment_type} details={w.payment_details} />
                  </div>

                  <div className="w-[13%] pr-2 text-xs">
                    {formatIST(w.created_at)}
                    {w.processed_at && (
                      <p className="text-[#858585]">Done: {formatIST(w.processed_at)}</p>
                    )}
                  </div>

                  <div className="w-[10%]">
                    <span
                      className={`px-3 py-1 text-xs font-semibold capitalize rounded-full ${
                        STATUS_STYLE[w.status] || "bg-[#F2F2F2] text-[#414141]"
                      }`}
                    >
                      {w.status}
                    </span>
                  </div>

                  <div className="w-[20%]">
                    {w.status === "pending" ? (
                      <div className="flex gap-2">
                        <button
                          disabled={actingId === w.id}
                          onClick={() => resolve(w, "approve")}
                          className="px-3 py-1.5 text-xs text-white rounded-md bg-button-gradient disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          disabled={actingId === w.id}
                          onClick={() => resolve(w, "reject")}
                          className="px-3 py-1.5 text-xs text-white bg-red-500 rounded-md disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      w.admin_note && (
                        <p className="text-xs text-[#858585]">Note: {w.admin_note}</p>
                      )
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6">
          <p className="text-sm font-poppins text-[#858585]">Total: {result?.total ?? 0}</p>
          <div className="flex items-center gap-3">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-1.5 text-sm font-poppins rounded-md bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-sm font-poppins dark:text-darkText">
              Page {page} / {lastPage}
            </span>
            <button
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-1.5 text-sm font-poppins rounded-md bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostCoinWithdrawals;
